import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../utils/createScopedI18n';
import { removeTime } from '../../../../utils/formatDate';
import { configTasksI18n } from './i18n';

export default function EditTaskQuickModal({
  DesignSystemComponents,
  show,
  onClose,
  onSave,
  task,
  users = [],
  statusOptions = [],
}) {
  const { language } = useLanguage();
  const configTasksLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: configTasksI18n });
  const { Dropdown, Input, Modal, ComboBox } = DesignSystemComponents;

  const [form, setForm] = useState({
    assignedUserId: '',
    dueDate: '',
    status: '',
  });

  useEffect(() => {
    configTasksI18n.changeLanguage(configTasksLanguage);
  }, [configTasksLanguage]);

  useEffect(() => {
    if (!show || !task) return;

    // Infer assigned user ID from different possible backend shapes
    const assignedUserId = String(
      task.abogadosAsignados?.[0]?.id ??
        task.usuarioAsignadoId ??
        task.userId ??
        task.usuarioId ??
        ''
    );

    // Accept multiple possible due date fields
    const dueDate =
      removeTime(task.fechaVencimiento) ||
      removeTime(task.due) ||
      task.dueDate ||
      task.deadline ||
      '';

    // Status ID or label possibly stored differently
    const status = String(task.estadoId ?? task.status ?? task.estado ?? '');

    setForm({
      assignedUserId: assignedUserId || '',
      dueDate: dueDate || '',
      status: status || '',
    });
  }, [show, task]);

  const isValid = form.assignedUserId && form.dueDate && form.status;

  const activeUserOptions = users.filter((u) => u.active === true);

  const userOptions = activeUserOptions.map((u) => ({
    label: u.label,
    value: u.value,
  }));

  const selectedUser =
    userOptions.find((opt) => opt.value === form.assignedUserId) || null;

  const handleSave = () => {
    if (!isValid) return;

    onSave(
      {
        id: task.id,
        assignedUserId: form.assignedUserId,
        dueDate: form.dueDate,
        status: form.status,
      },
      task
    );
  };

  const statusOpts = statusOptions.map((s) => ({
    label: s.label || s.name,
    value: s.value || s.id,
  }));

  const selectedStatus =
    statusOpts.find((opt) => opt.value === form.status) || null;

  return (
    <Modal
      title={t('configTasks_quickModal_title')}
      show={show}
      onClose={onClose}
      primaryBtnTxt={t('configTasks_quickModal_saveButton')}
      primaryBtnAction={handleSave}
      disabledPrimaryBtn={!isValid}
      secondaryBtnTxt={t('configTasks_quickModal_cancelButton')}
      secondaryBtnAction={onClose}
      ctaAligment='right'
      width='520px'
    >
      <div className='row'>
        <div className='col-12' style={{ marginBottom: '20px' }}>
          <ComboBox
            label={t('configTasks_quickModal_userLabel')}
            options={userOptions}
            value={selectedUser}
            onChange={(opt) => {
              setForm((prev) => ({
                ...prev,
                assignedUserId: opt?.value || '',
              }));
            }}
            placeholder={t('configTasks_quickModal_userPlaceholder')}
            width='100%'
          />
        </div>

        <div className='col-12'>
          <Input
            label={t('configTasks_quickModal_dueDateLabel')}
            type='date'
            value={form.dueDate}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, dueDate: e.target.value }))
            }
            required
          />
        </div>

        <div className='col-12'>
          <label className='form-label'>
            {t('configTasks_quickModal_statusLabel')}
          </label>
          <Dropdown
            options={statusOpts}
            value={selectedStatus}
            onChange={(opt) =>
              setForm((prev) => ({
                ...prev,
                status: opt?.value || '',
              }))
            }
            placeholder={t('configTasks_quickModal_statusPlaceholder')}
            width='100%'
          />
        </div>
      </div>
    </Modal>
  );
}
